import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Inputs that hold integer cents / basis points in state while people type
 * dollars / percentages. The conversion happens here, at the boundary.
 * Empty text reports null; invalid text reports undefined, so validation can
 * say so rather than the value being silently dropped or coerced.
 */
import { useEffect, useState } from 'react';
import { bpsToInput, centsToInput, textToBps, textToCents } from '../lib/money';
import { inputClass, inputErrorClass, cx } from './ui';
/** Empty -> null, unparseable -> undefined, otherwise the parsed integer. */
function readText(text, parse) {
    if (text.trim() === '')
        return null;
    return parse(text) ?? undefined;
}
function useSyncedText(value, toText, parse) {
    const [text, setText] = useState(() => toText(value));
    useEffect(() => {
        // Adopt an outside change (template applied, refetch) without fighting the person typing.
        setText((current) => (readText(current, parse) === value ? current : toText(value)));
    }, [value, toText, parse]);
    return [text, setText];
}
export const MoneyInput = ({ id, valueCents, onChange, error, disabled, placeholder, className, ...rest }) => {
    const [text, setText] = useSyncedText(valueCents, centsToInput, textToCents);
    return (_jsxs("div", { className: cx('relative', className), children: [_jsx("span", { "aria-hidden": "true", className: "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-on-surface-variant", children: "$" }), _jsx("input", { id: id, inputMode: "decimal", value: text, disabled: disabled, placeholder: placeholder, "aria-invalid": !!error, "aria-describedby": error ? `${id}-error` : undefined, onChange: (e) => {
                    setText(e.target.value);
                    onChange(readText(e.target.value, textToCents));
                }, className: cx(inputClass, 'pl-7 tabular-nums', error && inputErrorClass), ...rest })] }));
};
export const PercentInput = ({ id, valueBps, onChange, error, disabled, placeholder, className, ...rest }) => {
    const [text, setText] = useSyncedText(valueBps, bpsToInput, textToBps);
    return (_jsxs("div", { className: cx('relative', className), children: [_jsx("input", { id: id, inputMode: "decimal", value: text, disabled: disabled, placeholder: placeholder, "aria-invalid": !!error, "aria-describedby": error ? `${id}-error` : undefined, onChange: (e) => {
                    setText(e.target.value);
                    onChange(readText(e.target.value, textToBps));
                }, className: cx(inputClass, 'pr-8 tabular-nums', error && inputErrorClass), ...rest }), _jsx("span", { "aria-hidden": "true", className: "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-on-surface-variant", children: "%" })] }));
};
